// Pedido confirmado: número, cuándo llega y qué sigue. Claro y sin vueltas, como el "recibido" de un mensaje. En modo
// vendedor (decisión D60), lo que sigue es volver a la visita o a la lista del día.
import { formatoGs, MEDIO_COBRO_ETIQUETA } from '../compartido';
import { Icono } from '@reparto/ui';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Boton, ErrorCarga } from '../componentes/base';
import { EsqueletoConfirmado } from '../componentes/Esqueletos';
import { Pantalla } from '../componentes/Marco';
import { useConsulta } from '../datos/consultas';
import { useCuenta, useNegocio } from '../datos/cuenta';
import { fechaEntrega, plural } from '../datos/formato';
import { pedido } from '../datos/pedidos';
import { useVendedor } from '../datos/sesion';

export function Confirmado() {
  const { id = '' } = useParams();
  const perfil = useCuenta((s) => s.perfil)!;
  const navegar = useNavigate();
  const vendedor = useVendedor();
  const negocio = useNegocio();
  const c = useConsulta(`pedido:${id}`, () => pedido(id));
  const p = c.datos;
  const aprobado = p?.estado === 'aprobado';
  return (
    <Pantalla titulo={vendedor ? 'Pedido tomado' : 'Pedido enviado'}>
      <div className="px-4 pt-8 pb-6">
        {c.cargando ? (
          <EsqueletoConfirmado />
        ) : !p ? (
          <ErrorCarga mensaje={c.error} sinRed={c.sinRed} onReintentar={() => void c.recargar()} />
        ) : (
          <>
            <div className="flex flex-col items-center text-center">
              <span className="grid h-24 w-24 place-items-center rounded-full bg-emerald-100 text-emerald-700">
                <Icono nombre="check_circle" relleno tamano={60} />
              </span>
              <h2 className="mt-4 text-[28px] leading-tight font-bold text-stone-900">
                {vendedor
                  ? `¡Listo! Pedido de ${negocio?.nombre ?? 'tu cliente'}`
                  : aprobado
                    ? '¡Listo! Tu pedido está confirmado'
                    : '¡Listo! Recibimos tu pedido'}
              </h2>
              <p className="mt-2 text-lg text-stone-700">
                Pedido número <b className="whitespace-nowrap text-stone-900">{p.numero_visible}</b>
              </p>
            </div>

            <div className="mt-6 divide-y divide-stone-100 rounded-3xl border border-stone-200 bg-white shadow-sm">
              <Dato icono="local_shipping" titulo={vendedor ? 'Le llega' : 'Te llega'}>
                {fechaEntrega(p.fecha_entrega, perfil.empresa.zona_horaria)}
              </Dato>
              <Dato icono="inventory_2" titulo={vendedor ? 'Lleva' : 'Llevás'}>
                {plural(p.items.length, 'artículo', 'artículos')} · {plural(p.bultos, 'bulto', 'bultos')}
              </Dato>
              {p.monto !== null ? (
                <Dato icono="receipt" titulo="Total">
                  {formatoGs(p.monto)}
                </Dato>
              ) : null}
              <Dato icono="payments" titulo={vendedor ? 'Paga al recibir' : 'Pagás al recibir'}>
                {MEDIO_COBRO_ETIQUETA[p.cobro]}
              </Dato>
            </div>

            {!aprobado ? (
              <p className="mt-4 flex gap-2 rounded-2xl bg-sky-50 p-4 text-[17px] text-sky-950">
                <Icono nombre="fact_check" className="mt-0.5 shrink-0" />
                <span>
                  {perfil.empresa.nombre} lo va a revisar y confirmar. Mirá cómo va en <b>Mis pedidos</b>.
                </span>
              </p>
            ) : null}

            <div className="mt-6 flex flex-col gap-3">
              {vendedor ? (
                <Boton bloque tamano="lg" icono="route" onClick={() => navegar('/visitas', { replace: true })}>
                  Volver a mis visitas
                </Boton>
              ) : null}
              <Boton
                bloque
                tamano="lg"
                variante={vendedor ? 'secundario' : 'primario'}
                icono="receipt_long"
                onClick={() => navegar('/pedidos', { replace: true })}
              >
                Ver mis pedidos
              </Boton>
              {vendedor ? null : (
                <Boton
                  bloque
                  tamano="lg"
                  variante="secundario"
                  icono="storefront"
                  onClick={() => navegar('/', { replace: true })}
                >
                  Volver al inicio
                </Boton>
              )}
            </div>
          </>
        )}
      </div>
    </Pantalla>
  );
}

function Dato({ icono, titulo, children }: { icono: string; titulo: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 p-4">
      <Icono nombre={icono} tamano={28} className="shrink-0 text-primary-700" />
      <div className="min-w-0">
        <p className="text-[15px] text-stone-600">{titulo}</p>
        <p className="text-lg font-semibold text-stone-900">{children}</p>
      </div>
    </div>
  );
}
